"use client"

import { useClientTranslation } from "@/modules/App/components/i18n/i18n.client"
import { useMediaQuery } from "@/shared/hooks/use-media-query"
import { Pet } from "@/shared/types/global-store"
import { cn, Separator, Skeleton } from "@dalmatech/ui"
import { ChevronDown, ChevronUp } from "lucide-react"
import Image from "next/image"
import { useState } from "react"

import { getYearAndMonth, parsePetBirthday } from "../../helpers/helpers"
import { useBreeds } from "../../hooks/use-breeds"
import { DeletePetDrawer } from "../PetDrawer/delete-pet-drawer"
import { DeletePetDialog } from "../PetModal/delete-pet-modal"
import * as styles from "./tribe-card.styled"

type TribeCardProps = {
  pet: Pet
  deletePet: () => void
  editPet: () => void
  hasMultiplePets: boolean
  withPromotionBadge?: "loading" | boolean
}

export const TribeCard = ({ pet, deletePet, editPet, hasMultiplePets }: TribeCardProps) => {
  const { t } = useClientTranslation("tribe-page")
  const { data: breeds, isLoading: isBreedsLoading } = useBreeds()
  const isDesktop = useMediaQuery("(min-width: 640px)")
  const [isExpanded, setIsExpanded] = useState(false)

  const petBirthdate = parsePetBirthday(pet.birthday)
  const ageLabel = getYearAndMonth(
    (petBirthdate.getMonth() + 1).toString(),
    petBirthdate.getFullYear().toString()
  )
  const breedLabel =
    pet.breed.race === "mixed_breed"
      ? t("unknown-dog-breed")
      : pet.breed.race === "europeen"
        ? t("unknown-cat-breed")
        : isBreedsLoading || !breeds
          ? null
          : breeds.find((b) => b.value === pet.breed.race)?.label ?? ""
  const animalLabel = `${t(`card.${pet.type}`)} • ${t(`card.${pet.sexe.toLowerCase()}`)}`

  const deleteTrigger = (
    <button type="button" className={styles.deleteButton}>
      {t("delete-pet-modal.delete")}
    </button>
  )

  return (
    <div className={cn(styles.card, !isExpanded && styles.cardCollapsed)}>
      <button
        type="button"
        className={styles.header}
        onClick={() => setIsExpanded((prev) => !prev)}
        aria-expanded={isExpanded}
      >
      <div className={styles.headerWrapper}>

        <div className={styles.headerIcon}>
          <Image
            src={`/img/illus/tribe-${pet.type}.svg`}
            alt=""
            width={40}
            height={40}
            className="object-contain"
          />
        </div>
        <div className={styles.headerContent}>

          <div className={styles.title}>{pet.name}</div>
          {isExpanded ? (
            <ChevronUp className={styles.chevron} aria-hidden />
          ) : (
            <ChevronDown className={styles.chevron} aria-hidden />
          )}
        </div>
      </div>
      </button>

      {isExpanded && (
        <>
        <Separator variant="dashed" />
          <div className={styles.details}>
            <div className={styles.detailRow}>
              <span className={styles.detailLabel}>{t("card.animal")}</span>
              <span className={styles.detailValue}>{animalLabel}</span>
            </div>
            <div className={styles.detailRow}>
              <span className={styles.detailLabel}>{t("card.breed")}</span>
              {breedLabel === null ? (
                <Skeleton className={styles.breedSkeleton} />
              ) : (
                <span className={styles.detailValue}>{breedLabel}</span>
              )}
            </div>
            <div className={styles.detailRow}>
              <span className={styles.detailLabel}>{t("card.age")}</span>
              <span className={styles.detailValue}>{ageLabel}</span>
            </div>
          </div>

          <div className={styles.modifierButtonWrapper}>
            <button
              type="button"
              className={styles.modifierButton}
              onClick={editPet}
            >
              {t("edit-button")}
            </button>
            {hasMultiplePets &&
              (isDesktop ? (
                <DeletePetDialog
                  title={t("delete-pet-modal.title", { pet_name: pet.name })}
                  onClick={deletePet}
                  trigger={deleteTrigger}
                />
              ) : (
                <DeletePetDrawer
                  title={t("delete-pet-modal.title", { pet_name: pet.name })}
                  onClick={deletePet}
                  trigger={deleteTrigger}
                />
              ))}
          </div>
        </>
      )}
    </div>
  )
}
